import { Role } from 'auth';
import Big from 'big.js';
import classNames from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import omit from 'lodash/omit';
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { withPrivateAccess } from '@/modules/auth/hocs/withPrivateAccess.tsx';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { ArchiveSwitch } from '@/modules/common/components/ArchiveSwitch.tsx';
import { FiltersGroup } from '@/modules/common/components/FiltersGroup.tsx';
import RegisterClassesSelectPaginate from '@/modules/common/components/RegisterClassesSelectPaginate.tsx';
import { CreateReportModal } from '@/modules/common/components/modals/CreateReportModal.tsx';
import { BigRoundMode } from '@/modules/common/constants/big-round-mode.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DateVariant, dateToPrint } from '@/modules/common/utils/date-format.ts';
import { useRegisterClassListQuery } from '@/modules/investor-dashboard/queries/registers.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { getLastFinancialYearRange } from '@/modules/issuer-dashboard/components/holder-create/tabs/TaxReportHolder.tsx';
import { transactionsFilters } from '@/modules/issuer-dashboard/components/holder-tabs/TransactionsTab.tsx';
import { IssuerTransactionsOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { SelectHolder } from '@/modules/issuer-dashboard/components/transactions/SelectHolder.tsx';
import {
  TypeTransactions,
  useIssuerAddActivityMenuOptions,
} from '@/modules/issuer-dashboard/hooks/useIssuerAddActivityMenuOptions.ts';
import {
  useTransactionArchiveByIdMutation,
  useTransactionsListQuery,
} from '@/modules/issuer-dashboard/queries/transactions.ts';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import {
  BodyCell,
  Button,
  ConfirmModal,
  DropdownMenu,
  FilterItem,
  ModalProviderContext,
  PageLayout,
  Spinner,
  Table,
  TableBodyColumn,
  TableHeadColumn,
  TooltipNew,
  getSelectOptionByEnum,
} from 'ui';
import { useIsMobile, useSaveState } from 'ui/hooks';
import { TrashGrayIcon } from 'ui/icons';
import {
  fixNameEnum,
  goodFormatNumber,
  goodFormatUSDCurrency,
  goodFormatUSDCurrencyLowPrecision,
} from 'ui/utils';
import { ReportType, TransactionPaginateResponse, TransactionStatus, TransactionType } from 'api';

dayjs.extend(utc);

type TypeHead =
  | 'ID'
  | 'Status'
  | 'Class'
  | 'Type'
  | 'Holder'
  | 'Date'
  | 'Unit/Shares'
  | 'Price'
  | 'Total'
  | '';

const IssuerTransactions = () => {
  const [isDeleted, setIsDeleted] = useState<boolean | undefined>();
  return (
    <PageLayout
      title="Transactions"
      headerAction={
        <TransactionsHeaderAction
          setIsDeleted={setIsDeleted}
          isDeleted={isDeleted}
          inTransactionsPage={true}
        />
      }
      contentClassName="px-0 py-2"
      classNameTitle="flex-row justify-between items-center"
    >
      <TransactionsBody isDeleted={isDeleted} />
    </PageLayout>
  );
};

interface TransactionsHeaderActionProps {
  buttonName?: string;
  className?: string;
  classNameBlock?: string;
  openTypeTransaction?: TypeTransactions;
  fromPortfolio?: boolean;
  isDeleted?: boolean;
  setIsDeleted?: Dispatch<SetStateAction<boolean | undefined>>;
  inTransactionsPage?: boolean;
}

export const TransactionsHeaderAction = ({
  buttonName = 'Add transaction',
  className = 'w-[140px]',
  classNameBlock,
  openTypeTransaction,
  fromPortfolio = false,
  setIsDeleted,
  isDeleted,
  inTransactionsPage = false,
}: TransactionsHeaderActionProps) => {
  const { registerId, registerName } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const { openModal } = useContext(ModalProviderContext);
  const addActivityOptions = useIssuerAddActivityMenuOptions({
    registerId: Number(registerId),
    isPortfolio: fromPortfolio,
  });

  const { data: dataClasses, isLoading } = useRegisterClassListQuery(
    {
      page: 1,
      page_size: 50,
      register_id: +registerId,
    },
    !registerId,
  );

  const handleOpenCreateTransactionReportModal = useCallback(() => {
    const { date_from, date_to } = getLastFinancialYearRange();
    const start_date = dayjs(date_from).format('YYYY-MM-DD');
    const end_date = dayjs(date_to).format('YYYY-MM-DD');
    openModal(CreateReportModal, {
      reportType: ReportType.Transaction,
      registerClassIds: dataClasses?.data?.map(item => item.id),
      defaultNameReport: `Exported transactions for ${registerName}`,
      autoDownloadAfterGenerated: true,
      start_date,
      end_date,
    });
  }, [dataClasses?.data, openModal, registerName]);

  return (
    <div className={classNames('flex items-center gap-4', classNameBlock)}>
      {setIsDeleted && (
        <ArchiveSwitch
          active={!!isDeleted}
          onChange={active => setIsDeleted(active || undefined)}
        />
      )}

      {inTransactionsPage && (
        <Button
          loading={isLoading}
          disabled={isLoading}
          onClick={handleOpenCreateTransactionReportModal}
        >
          Export
        </Button>
      )}

      {!openTypeTransaction ? (
        <DropdownMenu
          items={addActivityOptions}
          button={<Button className={className}>{buttonName}</Button>}
          containerClassName={className}
          className="w-[160px] divide-white"
          placement="left-start"
        />
      ) : (
        <Button
          className={className}
          onClick={addActivityOptions.find(item => item.label === openTypeTransaction)?.onClick}
        >
          {buttonName}
        </Button>
      )}
    </div>
  );
};

export interface TransactionsBodyProps {
  rightBlock?: ReactNode;
  hideChooseRegisterClass?: boolean;
  register_class_id?: number;
  isDeleted?: boolean;
}

export const TransactionsBody = ({
  rightBlock,
  hideChooseRegisterClass = false,
  register_class_id,
  isDeleted,
}: TransactionsBodyProps) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const { openModal } = useContext(ModalProviderContext);
  const { registerId, registerCountry } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const [archivingId, setArchivingId] = useState<number | null>(null);

  const [filters, setFilters] = useSaveState<transactionsFilters>(
    {
      register_class_id,
      status: TransactionStatus.Completed,
    },
    `filters-transactions-${registerId}`,
  );

  const {
    data: issuerActivitiesList,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoadingActivitiesList,
    isFetching,
    isError,
  } = useTransactionsListQuery(
    {
      page: 1,
      page_size: 30,
      ...filters,
      register_class_id: register_class_id ?? filters.register_class_id,
      is_deleted: isDeleted,
    },
    false,
    Number(registerId),
  );

  const { mutateAsync: mutateAsyncTransactionArchive, isLoading: isLoadingTransactionArchive } =
    useTransactionArchiveByIdMutation();

  const getTypeTransaction = useCallback(
    (type?: TransactionType) => {
      if (type === TransactionType.Subscription)
        return getRegisterTypeStrings('Application', { capitalize: true });
      if (type === TransactionType.Drp) return 'Dividend reinvestment';
      return fixNameEnum(type);
    },
    [getRegisterTypeStrings],
  );

  const calculateTotal = useCallback((data: TransactionPaginateResponse): string => {
    const { status, type, total, price_per_share, quantity, register_class } = data;
    if (type === TransactionType.Drp) {
      if (
        status === TransactionStatus.Pending &&
        total &&
        price_per_share &&
        register_class?.current_price_per_share
      ) {
        const calculatedValue = new Big(total)
          .div(price_per_share)
          .mul(register_class.current_price_per_share)
          .round(2, BigRoundMode.roundHalfUp)
          .valueOf();
        return goodFormatUSDCurrencyLowPrecision(+calculatedValue);
      }

      if (
        status === TransactionStatus.DividendPending ||
        status === TransactionStatus.Unsuccessfull
      ) {
        return goodFormatUSDCurrencyLowPrecision(total ?? 0);
      }
    }

    const calculatedValue = new Big(quantity || 0)
      .mul(price_per_share || 0)
      .round(2, BigRoundMode.roundHalfUp)
      .valueOf();
    return goodFormatUSDCurrencyLowPrecision(+calculatedValue);
  }, []);

  const headItems = useMemo<TableHeadColumn<TypeHead>[]>(() => {
    let items: TableHeadColumn<TypeHead>[] = [
      { title: 'ID', className: '' },
      { title: 'Status', className: '' },
      {
        title: 'Class',
        className: '',
      },
      {
        title: 'Type',
        className: '',
      },
      {
        title: 'Holder',
        className: '',
      },
      {
        title: 'Date',
        className: 'min-w-[140px]',
      },
      {
        title: 'Unit/Shares',
        className: '',
      },
      {
        title: 'Price',
        className: '',
      },
      {
        title: 'Total',
        className: '',
      },
      {
        title: '',
      },
    ];

    if (hideChooseRegisterClass) {
      items = items.filter(item => item.title !== 'Class');
    }

    if (isDeleted) {
      items = items.slice(0, -1);
    }

    return items;
  }, [hideChooseRegisterClass, isDeleted]);

  const handleOpenOperationInfoModal = useCallback(
    (item: TransactionPaginateResponse) => {
      openModal(IssuerTransactionsOperationInfoModal, {
        id: item.id,
        registerId,
        registerCountry,
        isDeleted,
      });
    },
    [isDeleted, openModal, registerCountry, registerId],
  );

  const handleGoToHolderPage = useCallback(
    (entityId?: number) => {
      if (!entityId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.holder(entityId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const handleGoToClassPage = useCallback(
    (classId?: number) => {
      if (!classId) return;

      navigate(
        {
          pathname: issuerDashboardPaths.dynamic.classById(classId.toString()),
          search: params.toString(),
        },
        {
          state: { from: pathname + '?' + params.toString() },
        },
      );
    },
    [navigate, params, pathname],
  );

  const handleArchive = useCallback(
    (id: number) => {
      openModal(ConfirmModal, {
        title: 'Archive transaction?',
        onConfirm: async () => {
          try {
            setArchivingId(id);
            await mutateAsyncTransactionArchive(id);
          } finally {
            setArchivingId(null);
          }
        },
      });
    },
    [mutateAsyncTransactionArchive, openModal],
  );

  const bodyItems = useMemo<TableBodyColumn[]>(() => {
    if (!issuerActivitiesList) {
      return [];
    }

    let result: TableBodyColumn[] = [];

    issuerActivitiesList.pages.forEach(page => {
      result = result.concat(
        page.data?.map(data => {
          const total = calculateTotal(data);
          let body: BodyCell[] = [
            {
              cell: (
                <span className="text-sm leading-[22px] text-[#0369A1] font-[500]">#{data.id}</span>
              ),
            },
            {
              cell: (
                <span>
                  <ActivityStatusBadge status={data.status || ''} />
                </span>
              ),
            },
            {
              cell: (
                <span
                  onClick={e => {
                    e.stopPropagation();
                    handleGoToClassPage(data.register_class?.id);
                  }}
                  className={classNames(
                    'text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis',
                    {
                      'text-blue-700 font-medium cursor-pointer hover:underline':
                        !!data.register_class?.id,
                    },
                  )}
                >
                  {data.register_class?.class_name ?? '-'}
                </span>
              ),
            },
            {
              cell: <div className="">{getTypeTransaction(data.type)}</div>,
            },
            {
              cell: (
                <div className="flex flex-col">
                  <div
                    onClick={e => {
                      e.stopPropagation();
                      handleGoToHolderPage(data.shareholder_entity?.id);
                    }}
                    className={classNames(
                      'text-sm whitespace-nowrap max-w-[190px] overflow-hidden text-ellipsis',
                      {
                        'text-blue-700 font-medium cursor-pointer hover:underline':
                          !!data.shareholder_entity?.id,
                      },
                    )}
                  >
                    {data.shareholder_entity?.entity_name ?? '-'}
                  </div>
                </div>
              ),
            },
            {
              cell: (
                <div className="">
                  {dateToPrint(data.issue_date || data.requested_at_date, DateVariant.Table) || '-'}
                </div>
              ),
            },
            {
              cell: <span>{goodFormatNumber(data.quantity ?? 0)}</span>,
            },
            {
              cell: <span>{goodFormatUSDCurrency(data.price_per_share ?? 0)}</span>,
            },
            {
              cell: <span>{total}</span>,
            },
            {
              cell: (
                <span>
                  {isLoadingTransactionArchive && archivingId === data.id ? (
                    <Spinner size="sm" className="m-0" />
                  ) : (
                    <TooltipNew text="Archive" className="w-5 h-5 min-w-5 min-h-5">
                      <img
                        src={TrashGrayIcon}
                        alt="Archive"
                        className="w-5 h-5 min-w-5 min-h-5 cursor-pointer hover:opacity-75"
                        onClick={e => {
                          e.stopPropagation();
                          handleArchive(data.id);
                        }}
                      />
                    </TooltipNew>
                  )}
                </span>
              ),
            },
          ];

          if (hideChooseRegisterClass) {
            body = body.filter((_, i) => i !== 2); //remove class
          }
          if (isDeleted) {
            body = body.slice(0, -1); //remove archive
          }

          return {
            cells: body,
            onClick: () => handleOpenOperationInfoModal(data),
          };
        }),
      );
    });

    return result;
  }, [
    archivingId,
    calculateTotal,
    getTypeTransaction,
    handleArchive,
    handleGoToClassPage,
    handleGoToHolderPage,
    handleOpenOperationInfoModal,
    hideChooseRegisterClass,
    isDeleted,
    isLoadingTransactionArchive,
    issuerActivitiesList,
  ]);

  return (
    <div className="flex flex-col gap-2 w-full">
      <div
        data-mobile-actions
        data-mobile-toolbar
        className="min-w-0 flex flex-wrap sm:flex-nowrap items-start justify-between gap-4 w-full px-3 sm:px-6"
      >
        <FiltersPanel
          setFilters={setFilters}
          filters={filters}
          registerId={registerId}
          isFetching={isFetching}
          hideChooseRegisterClass={hideChooseRegisterClass}
        />
        {rightBlock}
      </div>
      <Table
        className="my-0"
        classNameHead="border-t-0"
        withRounded={isMobile}
        withBorder={true}
        withBorderX={false}
        headItems={headItems}
        items={bodyItems}
        isLoading={isLoadingActivitiesList}
        isError={isError}
        hasNextPageInfinite={hasNextPage}
        fetchNextPageInfinite={fetchNextPage}
        blockIdInfinite="scrollable"
      />
    </div>
  );
};

interface FiltersPanelProps {
  filters: transactionsFilters;
  setFilters: Dispatch<SetStateAction<transactionsFilters>>;
  registerId: string;
  isFetching: boolean;
  hideChooseRegisterClass?: boolean;
  forBuyBack?: boolean;
  forTransfers?: boolean;
}

export const FiltersPanel = ({
  filters,
  setFilters,
  registerId,
  isFetching,
  hideChooseRegisterClass = false,
  forBuyBack = false,
  forTransfers = false,
}: FiltersPanelProps) => {
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const handleChangeStatus = useCallback(
    (status: TransactionStatus | null) => {
      setFilters(filters => (status ? { ...filters, status } : omit(filters, 'status')));
    },
    [setFilters],
  );

  const handleChangeType = useCallback(
    (type: TransactionType | null) => {
      setFilters(filters => (type ? { ...filters, type } : omit(filters, 'type')));
    },
    [setFilters],
  );

  const typeOptions = useMemo(() => {
    return Object.values(TransactionType).map(value => {
      if (value === TransactionType.Drp) {
        return {
          label: 'Dividend reinvestment',
          value,
        };
      }

      if (value === TransactionType.Subscription) {
        return {
          label: getRegisterTypeStrings('application', { capitalize: true }),
          value,
        };
      }

      return {
        label: fixNameEnum(value),
        value,
      };
    });
  }, [getRegisterTypeStrings]);

  const dateValue =
    filters.requested_at_date_from && filters.requested_at_date_to
      ? ([dayjs(filters.requested_at_date_from), dayjs(filters.requested_at_date_to)] as [
          Dayjs,
          Dayjs,
        ])
      : undefined;

  const handleChangeDate = useCallback(
    (date: [Dayjs, Dayjs] | null) => {
      setFilters(filters => {
        return date
          ? {
              ...filters,
              requested_at_date_from: forBuyBack
                ? date[0].format('YYYY-MM-DD')
                : date[0].toISOString(),
              requested_at_date_to: forBuyBack
                ? date[1].format('YYYY-MM-DD')
                : date[1].endOf('d').toISOString(),
            }
          : omit(filters, ['requested_at_date_from', 'requested_at_date_to']);
      });
    },
    [forBuyBack, setFilters],
  );

  const handleChangeHolder = useCallback(
    (shareholder_id?: number) => {
      setFilters(filters => {
        return shareholder_id
          ? {
              ...filters,
              shareholder_id: +shareholder_id,
            }
          : omit(filters, ['shareholder_id']);
      });
    },
    [setFilters],
  );

  const handleChangeRegisterClass = useCallback(
    (registerClassId: number | null) => {
      setFilters(filters => {
        return registerClassId
          ? {
              ...filters,
              register_class_id: +registerClassId,
            }
          : omit(filters, ['register_class_id']);
      });
    },
    [setFilters],
  );

  return (
    <FiltersGroup variant="inline" className="flex gap-2 items-center flex-wrap w-full">
      {!forBuyBack && (
        <FilterItem
          placeholder="Status"
          type="select"
          value={filters.status}
          options={getSelectOptionByEnum<TransactionStatus>(TransactionStatus).map(item => ({
            ...item,
            label: item.label?.replace('Unsuccessfull', 'Unsuccessful'),
          }))}
          onChange={handleChangeStatus}
          className="rounded-lg"
          classes={{
            label: 'w-[85px] shrink-0',
            element: 'w-full min-w-[200px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
          }}
        />
      )}

      {!hideChooseRegisterClass && (
        <RegisterClassesSelectPaginate
          registerId={+registerId}
          onChange={item => {
            handleChangeRegisterClass(item?.value?.id || null);
          }}
          value={filters?.register_class_id}
          isMulti={false}
          placeholder="Class"
          label=""
          className="flex items-center border px-2 rounded-lg gap-2 w-full font-[Inter] text-sm leading-[22px] max-w-[350px] min-w-[200px]"
          classNameSelect="w-full"
          formFieldTitle=""
          showBorderBottom={false}
          withBorder={false}
          clearable={true}
        />
      )}

      {!forBuyBack && (
        <>
          {!forTransfers && (
            <FilterItem
              placeholder="Type"
              type="select"
              value={filters.type}
              options={typeOptions}
              onChange={handleChangeType}
              className="rounded-lg"
              classes={{
                label: 'w-[85px] shrink-0',
                element: 'w-full min-w-[220px] [&>div>div]:gap-1 [&>div>div]:md:!gap-3',
              }}
            />
          )}
          <SelectHolder
            setShareholder={handleChangeHolder}
            shareholderId={filters.shareholder_id}
            registerId={registerId}
            placeholder="Holder"
            label=""
            isClearable={true}
            className="max-w-[250px] min-w-[200px]"
          />
        </>
      )}

      <FilterItem
        placeholder="Date"
        type="date"
        onChange={handleChangeDate}
        value={dateValue}
        className="rounded-lg"
      />

      {isFetching && <Spinner size="md" variant="primary" />}
    </FiltersGroup>
  );
};

export default withPrivateAccess(IssuerTransactions, [Role.SR_ISSUER, Role.SR_ISSUER_ADMIN]);
